import { useAnnounce } from "@src/components/live-announcer";
import { NO_ESCAPE_OPTIONS } from "@src/raya-lucaria/i18n/interpolation";
import { Row, Table } from "@tanstack/react-table";
import { PaginationInfo } from "@types";
import { TFunction } from "i18next";
import { MutableRefObject, useEffect, useRef } from "react";

import { SelectionIntent } from "./context";
import { getColumnLabel, resolveRowLabel } from "./utils";

interface TableAnnouncementsOptions<T> {
  table: Table<T>;
  t: TFunction<"translation", undefined>;
  isLoading: boolean;
  pagination?: PaginationInfo;
  selectable: boolean;
  selectionIntent: MutableRefObject<SelectionIntent | null>;
}

type SelectionState = Record<string, boolean>;

const changedIds = (before: SelectionState, after: SelectionState) => {
  const ids = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...ids].filter((id) => !!before[id] !== !!after[id]);
};

export function useTableAnnouncements<T>({
  table,
  t,
  isLoading,
  pagination,
  selectable,
  selectionIntent,
}: TableAnnouncementsOptions<T>) {
  const announce = useAnnounce();
  const rows = table.getRowModel().rows;
  const isEmpty = !isLoading && rows.length === 0;

  const previousState = useRef({ isLoading, isEmpty });
  useEffect(() => {
    const was = previousState.current;
    previousState.current = { isLoading, isEmpty };
    if (!isEmpty) return;
    if (was.isLoading || !was.isEmpty) announce(t("emptyTableAnnouncement"));
  }, [announce, isEmpty, isLoading, t]);

  const currentPage = pagination?.current_page ?? 1;
  const pageCount = pagination?.count ?? 0;
  const pageSize = pagination?.per_page ?? 0;
  const totalItems = pagination?.total ?? 0;
  const totalPages = pagination?.total_pages ?? 1;
  const rangeMessage = (() => {
    if (isLoading || !pagination || totalItems === 0 || pageCount === 0) {
      return "";
    }
    const from = (currentPage - 1) * pageSize + 1;
    return t("paginationRange", {
      current: currentPage,
      total: totalPages,
      from,
      to: from + pageCount - 1,
      count: totalItems,
    });
  })();
  const rangeKey = `${currentPage}|${pageSize}|${totalItems}`;
  const announcedRangeKey = useRef<string | null>(
    rangeMessage !== "" ? rangeKey : null,
  );
  useEffect(() => {
    if (!rangeMessage) return;
    if (announcedRangeKey.current === null) {
      announcedRangeKey.current = rangeKey;
      return;
    }
    if (announcedRangeKey.current === rangeKey) return;
    announcedRangeKey.current = rangeKey;
    announce(rangeMessage);
  }, [announce, rangeKey, rangeMessage]);

  const sorting = table.getState().sorting;
  const sortKey = sorting.map(({ id, desc }) => `${id}:${desc}`).join(",");
  const previousSortKey = useRef(sortKey);
  const pendingSortMessage = useRef<string | null>(null);
  useEffect(() => {
    if (previousSortKey.current !== sortKey) {
      previousSortKey.current = sortKey;
      const [first] = sorting;
      const column = first ? table.getColumn(first.id) : undefined;
      if (first && column) {
        pendingSortMessage.current = first.desc
          ? t("sortedDescending", {
              column: getColumnLabel(column),
              ...NO_ESCAPE_OPTIONS,
            })
          : t("sortedAscending", {
              column: getColumnLabel(column),
              ...NO_ESCAPE_OPTIONS,
            });
      } else {
        pendingSortMessage.current = t("sortCleared");
      }
    }
    if (pendingSortMessage.current && !isLoading) {
      announce(pendingSortMessage.current);
      pendingSortMessage.current = null;
    }
  }, [announce, isLoading, sortKey, sorting, t, table]);

  const rowSelection = table.getState().rowSelection as SelectionState;
  const previousSelection = useRef<SelectionState>(rowSelection);
  const previousPage = useRef(currentPage);
  useEffect(() => {
    const before = previousSelection.current;
    previousSelection.current = rowSelection;
    const pageChanged = previousPage.current !== currentPage;
    previousPage.current = currentPage;
    if (before === rowSelection || !selectable) return;
    const intent = selectionIntent.current;
    selectionIntent.current = null;
    if (!intent || pageChanged) return;
    const changes = changedIds(before, rowSelection);
    if (changes.length === 0) return;
    const count = Object.values(rowSelection).filter(Boolean).length;
    const changedRow: Row<T> | undefined =
      intent === "row" && changes.length === 1
        ? rows.find((row) => row.id === changes[0])
        : undefined;
    announce(describeSelection(t, count, changedRow, table, rowSelection));
  }, [
    announce,
    currentPage,
    rowSelection,
    rows,
    selectable,
    selectionIntent,
    t,
    table,
  ]);
}

function describeSelection<T>(
  t: TFunction<"translation", undefined>,
  count: number,
  changedRow: Row<T> | undefined,
  table: Table<T>,
  rowSelection: SelectionState,
): string {
  if (changedRow) {
    const row = resolveRowLabel(t, table, changedRow);
    const selected = !!rowSelection[changedRow.id];
    if (selected) {
      return count === 1
        ? t("rowSelectedOne", { row, ...NO_ESCAPE_OPTIONS })
        : t("rowSelectedMany", { row, count, ...NO_ESCAPE_OPTIONS });
    }
    if (count === 0)
      return t("rowDeselectedNone", { row, ...NO_ESCAPE_OPTIONS });
    return count === 1
      ? t("rowDeselectedOne", { row, ...NO_ESCAPE_OPTIONS })
      : t("rowDeselectedMany", { row, count, ...NO_ESCAPE_OPTIONS });
  }
  if (count === 0) return t("selectionCleared");
  return count === 1 ? t("rowsSelectedOne") : t("rowsSelectedMany", { count });
}
